import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationBoolean,
    requirePresentationFields,
    requirePresentationNullableString,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import { attachAnchoredCalloutController } from "./anchored-callout-controller.js";

const BREAKDOWN_ROLES = new Set(["input", "detail", "result"]);
export const STAT_BREAKDOWN_PRESENTATION_PROPERTY = "calculatorStatPresentation";
export const STAT_BREAKDOWN_CALLOUT_ID = "fishy-stat-breakdown-tooltip";
const breakdownBindings = new WeakMap();
const tooltipRoots = new WeakSet();
const calloutControllers = new Set();

let bindingRevision = 0;
let tooltipElement = null;
let tooltipRefs = null;
let activeTooltipRenderState = null;
let activeTooltipPointer = null;

function configureStatBreakdownAnchor(anchor) {
    anchor?.setAttribute?.("aria-controls", STAT_BREAKDOWN_CALLOUT_ID);
    anchor?.setAttribute?.("aria-haspopup", "dialog");
    if (!anchor?.getAttribute?.("role")) {
        anchor?.setAttribute?.("role", "button");
    }
}

function parseFormulaToken(token, path) {
    if (token?.kind === "operator") {
        const value = requirePresentationFields(token, ["kind", "text"], path);
        return {
            kind: "operator",
            text: requirePresentationString(value.text, `${path}.text`),
        };
    }
    if (token?.kind === "term") {
        const value = requirePresentationFields(
            token,
            ["kind", "text", "valueText"],
            path,
        );
        return {
            kind: "term",
            text: requirePresentationString(value.text, `${path}.text`),
            valueText: requirePresentationString(
                value.valueText,
                `${path}.valueText`,
                { allowEmpty: true },
            ),
        };
    }
    throw new CalculatorPresentationContractError(`${path}.kind`, "expected operator or term");
}

function parseFormulaRow(row, path) {
    const value = requirePresentationFields(row, ["label", "tokens"], path);
    return {
        label: requirePresentationString(value.label, `${path}.label`, { allowEmpty: true }),
        tokens: requirePresentationArray(value.tokens, `${path}.tokens`)
            .map((token, index) => parseFormulaToken(token, `${path}.tokens[${index}]`)),
    };
}

function parseBreakdownRow(row, path) {
    if (row?.kind === "text") {
        const value = requirePresentationFields(
            row,
            ["detailText", "emphasis", "id", "kind", "label", "valueText"],
            path,
        );
        return {
            kind: "text",
            id: requirePresentationString(value.id, `${path}.id`),
            label: requirePresentationString(value.label, `${path}.label`),
            valueText: requirePresentationString(
                value.valueText,
                `${path}.valueText`,
                { allowEmpty: true },
            ),
            detailText: requirePresentationString(
                value.detailText,
                `${path}.detailText`,
                { allowEmpty: true },
            ),
            emphasis: requirePresentationBoolean(value.emphasis, `${path}.emphasis`),
        };
    }
    if (row?.kind === "item") {
        const value = requirePresentationFields(
            row,
            [
                "detailText",
                "emphasis",
                "gradeTone",
                "iconUrl",
                "id",
                "kind",
                "label",
                "valueText",
            ],
            path,
        );
        return {
            kind: "item",
            id: requirePresentationString(value.id, `${path}.id`),
            label: requirePresentationString(value.label, `${path}.label`),
            valueText: requirePresentationString(
                value.valueText,
                `${path}.valueText`,
                { allowEmpty: true },
            ),
            detailText: requirePresentationString(
                value.detailText,
                `${path}.detailText`,
                { allowEmpty: true },
            ),
            emphasis: requirePresentationBoolean(value.emphasis, `${path}.emphasis`),
            iconUrl: requirePresentationNullableString(value.iconUrl, `${path}.iconUrl`),
            gradeTone: requirePresentationString(value.gradeTone, `${path}.gradeTone`),
        };
    }
    throw new CalculatorPresentationContractError(`${path}.kind`, "expected text or item");
}

function parseBreakdownGroup(group, path) {
    const value = requirePresentationFields(group, ["id", "label", "rows"], path);
    return {
        id: requirePresentationString(value.id, `${path}.id`),
        label: requirePresentationString(value.label, `${path}.label`, { allowEmpty: true }),
        rows: requirePresentationArray(value.rows, `${path}.rows`)
            .map((row, index) => parseBreakdownRow(row, `${path}.rows[${index}]`)),
    };
}

function parseBreakdownSection(section, path) {
    const value = requirePresentationFields(section, ["groups", "id", "label", "role"], path);
    if (!BREAKDOWN_ROLES.has(value.role)) {
        throw new CalculatorPresentationContractError(
            `${path}.role`,
            "expected input, detail, or result",
        );
    }
    return {
        id: requirePresentationString(value.id, `${path}.id`),
        label: requirePresentationString(value.label, `${path}.label`),
        role: value.role,
        groups: requirePresentationArray(value.groups, `${path}.groups`)
            .map((group, index) => parseBreakdownGroup(group, `${path}.groups[${index}]`)),
    };
}

/**
 * Strict presentation-only contract for a calculator stat tooltip.
 * Labels, formatting, grouping, emphasis, and formula tokens are supplied by
 * the presentation owner; this module never infers them from formula prose.
 */
export function parseStatBreakdownPresentation(presentation) {
    const value = requirePresentationFields(
        presentation,
        [
            "color",
            "eyebrow",
            "formulaLabel",
            "formulaRows",
            "id",
            "sections",
            "summaryText",
            "title",
            "valueText",
        ],
        "$.statBreakdown",
    );
    return {
        id: requirePresentationString(value.id, "$.statBreakdown.id"),
        color: requirePresentationString(value.color, "$.statBreakdown.color"),
        eyebrow: requirePresentationString(value.eyebrow, "$.statBreakdown.eyebrow"),
        title: requirePresentationString(value.title, "$.statBreakdown.title"),
        valueText: requirePresentationString(
            value.valueText,
            "$.statBreakdown.valueText",
            { allowEmpty: true },
        ),
        summaryText: requirePresentationString(
            value.summaryText,
            "$.statBreakdown.summaryText",
            { allowEmpty: true },
        ),
        formulaLabel: requirePresentationString(
            value.formulaLabel,
            "$.statBreakdown.formulaLabel",
        ),
        formulaRows: requirePresentationArray(value.formulaRows, "$.statBreakdown.formulaRows")
            .map((row, index) => parseFormulaRow(row, `$.statBreakdown.formulaRows[${index}]`)),
        sections: requirePresentationArray(value.sections, "$.statBreakdown.sections")
            .map((section, index) => parseBreakdownSection(
                section,
                `$.statBreakdown.sections[${index}]`,
            )),
    };
}

/** Assigns or clears one anchor's explicit presentation property. */
export function setStatBreakdownPresentation(anchor, presentation) {
    if (!anchor || (typeof anchor !== "object" && typeof anchor !== "function")) {
        throw new TypeError("stat breakdown anchor must be an object");
    }
    if (presentation === null || presentation === undefined) {
        anchor[STAT_BREAKDOWN_PRESENTATION_PROPERTY] = null;
        breakdownBindings.delete(anchor);
        for (const controller of calloutControllers) {
            controller.dismissAnchor(anchor);
        }
        return null;
    }
    const parsed = parseStatBreakdownPresentation(presentation);
    bindingRevision += 1;
    anchor[STAT_BREAKDOWN_PRESENTATION_PROPERTY] = parsed;
    breakdownBindings.set(anchor, {
        raw: parsed,
        presentation: parsed,
        revision: bindingRevision,
    });
    configureStatBreakdownAnchor(anchor);
    if (anchor.getAttribute?.("aria-expanded") === null) {
        anchor.setAttribute?.("aria-expanded", "false");
    }
    for (const controller of calloutControllers) {
        controller.refreshAnchor(anchor);
    }
    return parsed;
}

export function statBreakdownPresentationForAnchor(anchor) {
    if (!anchor || !Object.prototype.hasOwnProperty.call(anchor, STAT_BREAKDOWN_PRESENTATION_PROPERTY)) {
        return null;
    }
    const raw = anchor[STAT_BREAKDOWN_PRESENTATION_PROPERTY];
    if (raw === null || raw === undefined) {
        breakdownBindings.delete(anchor);
        return null;
    }
    const cached = breakdownBindings.get(anchor);
    if (cached?.raw === raw) {
        return cached.presentation;
    }
    const parsed = parseStatBreakdownPresentation(raw);
    bindingRevision += 1;
    breakdownBindings.set(anchor, { raw, presentation: parsed, revision: bindingRevision });
    return parsed;
}

export function statBreakdownTargetFromEvent(eventTarget) {
    let current = eventTarget;
    while (current) {
        if (statBreakdownPresentationForAnchor(current)) {
            return current;
        }
        current = current.parentNode ?? current.host ?? null;
    }
    return null;
}

export function statBreakdownTooltipRenderKey(anchor) {
    statBreakdownPresentationForAnchor(anchor);
    const binding = breakdownBindings.get(anchor);
    return binding ? `${binding.presentation.id}:${binding.revision}` : "";
}

export function statBreakdownTooltipShouldRefresh(renderState, tooltip, anchor) {
    const renderKey = statBreakdownTooltipRenderKey(anchor);
    return {
        renderKey,
        shouldRefresh: Boolean(renderKey)
            && (renderState?.tooltip !== tooltip || renderState?.renderKey !== renderKey),
    };
}

function tooltipPointerSnapshot(event) {
    const clientX = Number(event?.clientX);
    const clientY = Number(event?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        return null;
    }
    return { clientX, clientY };
}

export function statBreakdownTooltipPointerForAnchor(anchor, event, activePointer = null) {
    const pointer = tooltipPointerSnapshot(event);
    if (pointer) {
        return { ...pointer, anchor };
    }
    if (activePointer?.anchor === anchor) {
        return activePointer;
    }
    return null;
}

export function statBreakdownTooltipAnchorPoint(anchor, position = null, fallbackPosition = null) {
    let clientX = Number(position?.clientX);
    let clientY = Number(position?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        clientX = Number(fallbackPosition?.clientX);
        clientY = Number(fallbackPosition?.clientY);
    }
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        const rect = typeof anchor?.getBoundingClientRect === "function"
            ? anchor.getBoundingClientRect()
            : null;
        clientX = Number(rect?.left ?? 0) + Number(rect?.width ?? 0) / 2;
        clientY = Number(rect?.top ?? 0) + Number(rect?.height ?? 0) / 2;
    }
    return { clientX, clientY };
}

function ensureTooltipElement() {
    if (tooltipElement?.isConnected && tooltipRefs) {
        return { tooltip: tooltipElement, refs: tooltipRefs };
    }
    const documentRef = globalThis.document;
    if (!documentRef?.body || typeof documentRef.createElement !== "function") {
        return null;
    }

    const tooltip = documentRef.createElement("div");
    tooltip.className = "fishy-stat-breakdown-tooltip";
    tooltip.hidden = true;
    tooltip.setAttribute("id", STAT_BREAKDOWN_CALLOUT_ID);
    tooltip.setAttribute("role", "tooltip");
    tooltip.setAttribute("tabindex", "-1");

    const eyebrow = documentRef.createElement("div");
    eyebrow.className = "fishy-stat-breakdown-tooltip__eyebrow";
    const swatch = documentRef.createElement("span");
    swatch.className = "fishy-stat-breakdown-tooltip__swatch";
    swatch.setAttribute("aria-hidden", "true");
    const eyebrowLabel = documentRef.createElement("span");
    eyebrowLabel.className = "fishy-stat-breakdown-tooltip__eyebrow-label";
    eyebrow.append(swatch, eyebrowLabel);

    const header = documentRef.createElement("div");
    header.className = "fishy-stat-breakdown-tooltip__header";
    const title = documentRef.createElement("div");
    title.className = "fishy-stat-breakdown-tooltip__title";
    const value = documentRef.createElement("div");
    value.className = "fishy-stat-breakdown-tooltip__value";
    header.append(title, value);

    const summary = documentRef.createElement("div");
    summary.className = "fishy-stat-breakdown-tooltip__summary";

    const formula = documentRef.createElement("div");
    formula.className = "fishy-stat-breakdown-tooltip__formula";
    const formulaLabel = documentRef.createElement("div");
    formulaLabel.className = "fishy-stat-breakdown-tooltip__formula-label";
    const formulaRows = documentRef.createElement("div");
    formulaRows.className = "fishy-stat-breakdown-tooltip__formula-rows";
    formula.append(formulaLabel, formulaRows);

    const sections = documentRef.createElement("div");
    sections.className = "fishy-stat-breakdown-tooltip__sections";

    tooltip.append(eyebrow, header, summary, formula, sections);
    documentRef.body.appendChild(tooltip);
    tooltipElement = tooltip;
    tooltipRefs = {
        eyebrowLabel,
        formula,
        formulaLabel,
        formulaRows,
        sections,
        summary,
        title,
        value,
    };
    return { tooltip, refs: tooltipRefs };
}

function itemToneClass(gradeTone) {
    return `fishy-item-grade-${gradeTone}`;
}

function itemFallbackLabel(label) {
    return label.charAt(0).toUpperCase();
}

function buildFormulaToken(documentRef, token) {
    if (token.kind === "operator") {
        const operator = documentRef.createElement("span");
        operator.className = "fishy-stat-breakdown-tooltip__formula-operator";
        operator.textContent = token.text;
        return operator;
    }

    const term = documentRef.createElement("span");
    term.className = "fishy-stat-breakdown-tooltip__formula-term";
    const label = documentRef.createElement("span");
    label.className = "fishy-stat-breakdown-tooltip__formula-term-label";
    label.textContent = token.text;
    term.appendChild(label);
    if (token.valueText) {
        term.classList.add("fishy-stat-breakdown-tooltip__formula-term--resolved");
        const value = documentRef.createElement("span");
        value.className = "fishy-stat-breakdown-tooltip__formula-term-value";
        value.textContent = token.valueText;
        term.appendChild(value);
    }
    return term;
}

function buildFormulaRow(documentRef, row) {
    const element = documentRef.createElement("div");
    element.className = "fishy-stat-breakdown-tooltip__formula-row";
    if (row.label) {
        const label = documentRef.createElement("div");
        label.className = "fishy-stat-breakdown-tooltip__formula-row-label";
        label.textContent = row.label;
        element.appendChild(label);
    }
    const tokens = documentRef.createElement("div");
    tokens.className = "fishy-stat-breakdown-tooltip__formula-tokens";
    tokens.append(...row.tokens.map((token) => buildFormulaToken(documentRef, token)));
    element.appendChild(tokens);
    return element;
}

function buildRowMain(documentRef, row, showDetail) {
    const main = documentRef.createElement("div");
    main.className = "fishy-stat-breakdown-tooltip__row-main";

    if (row.kind === "item") {
        const toneClass = itemToneClass(row.gradeTone);
        const itemRow = documentRef.createElement("span");
        itemRow.className = `fishy-stat-breakdown-tooltip__item-row fishy-item-row ${toneClass}`;
        const iconFrame = documentRef.createElement("span");
        iconFrame.className = `fishy-stat-breakdown-tooltip__item-icon-frame fishy-item-icon-frame is-xs ${toneClass}`;
        if (row.iconUrl) {
            const icon = documentRef.createElement("img");
            icon.className = "fishy-stat-breakdown-tooltip__item-icon fishy-item-icon item-icon";
            icon.src = row.iconUrl;
            icon.alt = `${row.label} icon`;
            icon.loading = "lazy";
            icon.decoding = "async";
            iconFrame.appendChild(icon);
        } else {
            const fallback = documentRef.createElement("span");
            fallback.className = `fishy-item-icon-fallback ${toneClass}`;
            fallback.textContent = itemFallbackLabel(row.label);
            iconFrame.appendChild(fallback);
        }
        const label = documentRef.createElement("span");
        label.className = `fishy-stat-breakdown-tooltip__item-label fishy-domain-label fishy-item-label ${toneClass}`;
        label.textContent = row.label;
        itemRow.append(iconFrame, label);
        main.appendChild(itemRow);
    } else {
        const label = documentRef.createElement("div");
        label.className = "fishy-stat-breakdown-tooltip__row-label";
        label.textContent = row.label;
        main.appendChild(label);
    }

    if (showDetail && row.detailText) {
        const detail = documentRef.createElement("div");
        detail.className = "fishy-stat-breakdown-tooltip__row-detail";
        detail.textContent = row.detailText;
        main.appendChild(detail);
    }
    return main;
}

function buildSection(documentRef, section, index) {
    const sectionElement = documentRef.createElement("section");
    sectionElement.className = [
        "fishy-stat-breakdown-tooltip__section",
        section.role === "result" ? "fishy-stat-breakdown-tooltip__section--result" : "",
    ].filter(Boolean).join(" ");

    const heading = documentRef.createElement("div");
    heading.className = index === 0
        ? "fishy-stat-breakdown-tooltip__section-title"
        : "divider divider-neutral fishy-stat-breakdown-tooltip__section-divider";
    heading.textContent = section.label;
    sectionElement.appendChild(heading);

    for (const group of section.groups) {
        const groupElement = documentRef.createElement("div");
        groupElement.className = [
            "fishy-stat-breakdown-tooltip__group",
            section.role === "input" ? "fishy-stat-breakdown-tooltip__group--input" : "",
            section.role === "result" ? "fishy-stat-breakdown-tooltip__group--result" : "",
            group.label ? "fishy-stat-breakdown-tooltip__group--titled" : "",
        ].filter(Boolean).join(" ");
        if (group.label) {
            const groupTitle = documentRef.createElement("div");
            groupTitle.className = "fishy-stat-breakdown-tooltip__group-title";
            groupTitle.textContent = group.label;
            groupElement.appendChild(groupTitle);
        }
        for (const row of group.rows) {
            const rowElement = documentRef.createElement("div");
            rowElement.className = [
                "fishy-stat-breakdown-tooltip__row",
                row.emphasis ? "fishy-stat-breakdown-tooltip__row--emphasis" : "",
            ].filter(Boolean).join(" ");
            const value = documentRef.createElement("div");
            value.className = "fishy-stat-breakdown-tooltip__row-value";
            value.textContent = row.valueText;
            rowElement.append(buildRowMain(documentRef, row, section.role !== "result"), value);
            groupElement.appendChild(rowElement);
        }
        sectionElement.appendChild(groupElement);
    }
    return sectionElement;
}

function boundedTooltipAxis({
    alternate,
    margin,
    preferred,
    size,
    viewportSize,
}) {
    if (!Number.isFinite(viewportSize) || viewportSize <= 0) {
        return preferred;
    }
    const maximum = Math.max(margin, viewportSize - size - margin);
    const candidate = preferred + size + margin <= viewportSize
        ? preferred
        : alternate;
    return Math.min(maximum, Math.max(margin, candidate));
}

export function statBreakdownTooltipViewportPosition({
    anchorPoint,
    tooltipRect,
    viewportHeight,
    viewportWidth,
}) {
    const margin = 12;
    const offsetX = 14;
    const offsetY = 18;
    const width = Math.max(0, Number(tooltipRect?.width ?? 0));
    const height = Math.max(0, Number(tooltipRect?.height ?? 0));
    return {
        x: boundedTooltipAxis({
            alternate: anchorPoint.clientX - width - margin,
            margin,
            preferred: anchorPoint.clientX + offsetX,
            size: width,
            viewportSize: Number(viewportWidth),
        }),
        y: boundedTooltipAxis({
            alternate: anchorPoint.clientY - height - margin,
            margin,
            preferred: anchorPoint.clientY + offsetY,
            size: height,
            viewportSize: Number(viewportHeight),
        }),
    };
}

function updateTooltipPosition(tooltip, anchor, position = null, fallbackPosition = null) {
    const windowRef = globalThis.window;
    if (!tooltip || !windowRef) {
        return;
    }
    const anchorPoint = statBreakdownTooltipAnchorPoint(anchor, position, fallbackPosition);
    tooltip.style.left = "0";
    tooltip.style.top = "0";
    tooltip.style.transform = `translate3d(${anchorPoint.clientX + 14}px, ${anchorPoint.clientY + 18}px, 0)`;
    const tooltipRect = typeof tooltip.getBoundingClientRect === "function"
        ? tooltip.getBoundingClientRect()
        : null;
    const positionInViewport = statBreakdownTooltipViewportPosition({
        anchorPoint,
        tooltipRect,
        viewportHeight: windowRef.innerHeight,
        viewportWidth: windowRef.innerWidth,
    });
    tooltip.style.transform = `translate3d(${positionInViewport.x}px, ${positionInViewport.y}px, 0)`;
}

function presentStatBreakdown(anchor) {
    const payload = statBreakdownPresentationForAnchor(anchor);
    const tooltipData = ensureTooltipElement();
    if (!payload || !tooltipData) {
        return null;
    }
    const { tooltip, refs } = tooltipData;
    const refreshState = statBreakdownTooltipShouldRefresh(activeTooltipRenderState, tooltip, anchor);
    if (refreshState.shouldRefresh) {
        const documentRef = globalThis.document;
        refs.eyebrowLabel.textContent = payload.eyebrow;
        refs.title.textContent = payload.title;
        refs.value.textContent = payload.valueText;
        refs.value.hidden = !payload.valueText;
        refs.summary.textContent = payload.summaryText;
        refs.summary.hidden = !payload.summaryText;
        refs.formulaLabel.textContent = payload.formulaLabel;
        refs.formulaRows.replaceChildren(
            ...payload.formulaRows.map((row) => buildFormulaRow(documentRef, row)),
        );
        refs.formula.hidden = payload.formulaRows.length === 0;
        refs.sections.replaceChildren(
            ...payload.sections.map((section, index) => buildSection(documentRef, section, index)),
        );
        refs.sections.hidden = payload.sections.length === 0;
        tooltip.style.setProperty("--fishy-stat-breakdown-color", payload.color);
        activeTooltipRenderState = { tooltip, renderKey: refreshState.renderKey };
    }
    return {
        dialogLabel: payload.title,
        focusTarget: tooltip,
        panel: tooltip,
    };
}

export function attachStatBreakdownTooltip(root) {
    if (!root || tooltipRoots.has(root) || typeof root.addEventListener !== "function") {
        return;
    }
    tooltipRoots.add(root);
    const controller = attachAnchoredCalloutController(root, {
        id: STAT_BREAKDOWN_CALLOUT_ID,
        defaultAnchorRole: "button",
        fallbackDialogLabel: "Calculation details",
        panel: () => tooltipElement,
        pinnedAttribute: "data-fishy-stat-breakdown-callout-pinned",
        present: presentStatBreakdown,
        resolveAnchor: statBreakdownTargetFromEvent,
        onAnchorChanged({ panel }) {
            panel.scrollTop = 0;
        },
        onDismiss() {
            activeTooltipRenderState = null;
            activeTooltipPointer = null;
        },
        position({ panel }, anchor, event) {
            const pointer = statBreakdownTooltipPointerForAnchor(
                anchor,
                event,
                activeTooltipPointer,
            );
            if (pointer) {
                activeTooltipPointer = pointer;
            }
            updateTooltipPosition(panel, anchor, pointer, event);
        },
    });
    if (!controller) {
        return null;
    }
    const lifecycle = Object.freeze({
        ...controller,
        dispose() {
            calloutControllers.delete(lifecycle);
            tooltipRoots.delete(root);
            controller.dispose();
        },
    });
    calloutControllers.add(lifecycle);
    return lifecycle;
}
